/**
 * Header Component
 * AIDLC Administrative Backend
 *
 * Top header bar (48px height) with:
 * - Current page title (from route)
 * - WebSocket connection status indicator
 * - LIVE badge when bridge is active and WebSocket connected
 * - "Last updated X seconds ago" timestamp
 * - Quick actions (Take Tour button, etc.)
 *
 * Fixed position, sits to the right of the sidebar (margin-left: 220px).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNotifications, getUnreadCount, markNotificationRead, markAllRead } from '../../services/notificationService';
import type { AppNotification } from '../../types/backlog';

interface HeaderProps {
  isWsConnected: boolean;
  bridgeActive?: boolean;
  lastBridgeUpdate?: string | null;
  onStartTour?: () => void;
  socket?: { on: (e: string, cb: (d: unknown) => void) => void; off: (e: string, cb: (d: unknown) => void) => void } | null;
}

const pageTitles: Record<string, string> = {
  '/': 'Agent Dashboard',
  '/hiring': 'Hiring Pipeline',
  '/projects': 'Project Management',
  '/projects/overview': 'Projects Overview',
  '/governance': 'Governance & Compliance',
  '/control': 'Control Room',
  '/lifecycle': 'AIDLC Lifecycle',
  '/chat': 'Chat Interface',
  '/audit': 'Audit & History',
  '/templates': 'Templates',
  '/settings': 'Settings',
  '/backlog': 'Backlog',
  '/tokens': 'Token Management',
  // New feature pages
  '/proposals': 'Budget Proposals',
  '/roadmap': 'Token Roadmap',
  '/milestones': 'Milestone Tracker',
  '/scheduler': 'Agent Scheduler',
  '/spawn-planner': 'Spawn Planner',
  '/assessments': 'Assessment Reports',
  '/burn-monitor': 'Budget Burn Monitor',
  '/escalations': 'Escalation Queue',
  '/dod-pipeline': 'DoD Pipeline',
  '/estimation': 'Token Estimation',
  '/review-reports': 'Review Reports',
};

function formatTimeAgo(isoTimestamp: string): string {
  const now = Date.now();
  const then = new Date(isoTimestamp).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 5) return 'just now';
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  return `${Math.floor(diffSeconds / 86400)}d ago`;
}

export function Header({ isWsConnected, bridgeActive, lastBridgeUpdate, onStartTour, socket }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const pageTitle = pageTitles[location.pathname] ?? 'AIDLC Admin';

  // Re-render every 5 seconds to update the "X seconds ago" display
  useEffect(() => {
    if (!lastBridgeUpdate) return;
    const interval = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(interval);
  }, [lastBridgeUpdate]);

  // Load unread count on mount + periodically
  useEffect(() => {
    getUnreadCount().then(setUnreadCount).catch(() => {});
    const interval = setInterval(() => {
      getUnreadCount().then(setUnreadCount).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Listen for real-time notifications via WebSocket
  useEffect(() => {
    if (!socket) return;
    const handler = (notif: unknown) => {
      const n = notif as AppNotification;
      setUnreadCount(c => c + 1);
      setNotifications(prev => [n, ...prev].slice(0, 50));
    };
    socket.on('notification:new', handler);
    return () => { socket.off('notification:new', handler); };
  }, [socket]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const openNotifications = useCallback(async () => {
    setNotifOpen(o => !o);
    if (!notifOpen) {
      setNotifLoading(true);
      try {
        const all = await getNotifications();
        setNotifications(all.slice(0, 50));
      } catch { /* silent */ }
      setNotifLoading(false);
    }
  }, [notifOpen]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* silent */ }
  }, []);

  const handleNotifClick = useCallback(async (notif: AppNotification) => {
    if (!notif.read) {
      try {
        await markNotificationRead(notif.id);
        setUnreadCount(c => Math.max(0, c - 1));
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      } catch { /* silent */ }
    }
    if (notif.link) {
      navigate(notif.link);
      setNotifOpen(false);
    }
  }, [navigate]);

  const isLive = isWsConnected && bridgeActive;

  return (
    <header className="fixed top-0 left-[220px] right-0 h-12 bg-white border-b border-gray-200 flex items-center justify-between px-6 z-40">
      {/* Page Title */}
      <h2 className="text-base font-semibold text-ldaja-black">{pageTitle}</h2>

      {/* Right side indicators */}
      <div className="flex items-center gap-4">
        {/* Notification Bell */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={openNotifications}
            className="relative p-1.5 text-gray-500 hover:text-ldaja-blue hover:bg-gray-100 rounded-md transition-colors"
            title="Notifications"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown */}
          {notifOpen && (
            <div className="absolute right-0 top-10 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[400px] flex flex-col">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <span className="text-sm font-semibold text-ldaja-black">Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} className="text-[10px] text-ldaja-blue hover:underline">
                    Mark all read
                  </button>
                )}
              </div>
              <div className="overflow-y-auto flex-1">
                {notifLoading ? (
                  <div className="p-4 text-center text-xs text-gray-400">Loading...</div>
                ) : notifications.length === 0 ? (
                  <div className="p-6 text-center text-xs text-gray-400">No notifications yet</div>
                ) : (
                  notifications.map(n => (
                    <button
                      key={n.id}
                      onClick={() => handleNotifClick(n)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                        !n.read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="w-2 h-2 rounded-full bg-ldaja-blue flex-shrink-0 mt-1.5" />}
                        <div className={!n.read ? '' : 'ml-4'}>
                          <p className="text-xs font-medium text-ldaja-black">{n.title}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{formatTimeAgo(n.createdAt)}</p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Take Tour Button */}
        {onStartTour && (
          <button
            onClick={onStartTour}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ldaja-blue bg-ldaja-blue/5 border border-ldaja-blue/20 rounded-md hover:bg-ldaja-blue/10 hover:border-ldaja-blue/40 transition-colors"
            title="Take a guided tour of the AIDLC Admin portal"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Take Tour
          </button>
        )}

        {/* LIVE Badge - shown when WebSocket connected AND bridge active */}
        {isLive && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            <span className="text-xs font-semibold text-green-700 tracking-wide">LIVE</span>
          </div>
        )}

        {/* Bridge Activity - last updated timestamp */}
        {lastBridgeUpdate && (
          <span className="text-xs text-gray-400" title={`Last bridge activity: ${lastBridgeUpdate}`}>
            Updated {formatTimeAgo(lastBridgeUpdate)}
          </span>
        )}

        {/* WebSocket Status */}
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`w-2 h-2 rounded-full ${
              isWsConnected ? 'bg-green-500' : 'bg-red-500'
            }`}
          />
          <span className="text-gray-500">
            {isWsConnected ? (bridgeActive ? 'Bridge Connected' : 'Live') : 'Offline'}
          </span>
        </div>

        {/* Version */}
        <span className="text-xs text-gray-400">
          AIDLC v2.0.0
        </span>
      </div>
    </header>
  );
}
